import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Assuming you have these icons in src/components/Icons.tsx
// If not, you can replace <EyeOffIcon /> and <EyeIcon /> with text
// import { EyeIcon, EyeOffIcon } from './Icons'; 

// Placeholder icons if you don't have them
const EyeIcon = () => <span>Show</span>;
const EyeOffIcon = () => <span>Hide</span>;

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
       setError('New password must be at least 8 characters long.');
       return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Authentication error. Please log in again.');
      return;
    }

    setIsLoading(true);
    try {
      // Use PUT for your /api/users/me/password endpoint
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      await axios.put(`${API_BASE_URL}/api/users/me/password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      onSuccess(); // Call the success handler from SettingsPage

    } catch (err: any) {
      console.error("Password change error:", err);
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else if (err.response.status === 400 || err.response.status === 422) {
          setError(err.response.data.detail || 'Incorrect current password or invalid new password.');
        } else {
          setError('An unexpected error occurred.');
        }
      } else {
        setError('Failed to connect to the server.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form state when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setError('');
      setIsLoading(false);
    }
  }, [isOpen]);


  if (!isOpen) {
    return null;
  }

  // --- Styles ---
  const styles: { [key: string]: React.CSSProperties } = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modal: { backgroundColor: 'white', borderRadius: '8px', padding: '2rem', width: '100%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' },
    title: { margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' },
    formGroup: { marginBottom: '1rem' },
    label: { display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#475569', fontWeight: 500 },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    input: { width: '100%', padding: '0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '1rem', paddingRight: '2.5rem' },
    toggleButton: { position: 'absolute', right: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '0.25rem' },
    errorMessage: { color: '#dc2626', fontSize: '0.875rem', marginTop: '1rem', textAlign: 'center', minHeight: '1.2em' },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' },
    button: { padding: '0.6rem 1.2rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' },
    cancelButton: { backgroundColor: '#f1f5f9', color: '#475569' },
    saveButton: { backgroundColor: '#3b82f6', color: 'white', opacity: isLoading ? 0.6 : 1 },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div style={styles.header}>
            <h2 style={styles.title}>Change Password</h2>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="currentPassword">Current Password</label>
            <div style={styles.inputWrapper}>
              <input style={styles.input} id="currentPassword" type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required disabled={isLoading} autoComplete="current-password" />
              <button type="button" style={styles.toggleButton} onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="newPassword">New Password</label>
            <div style={styles.inputWrapper}>
              <input style={styles.input} id="newPassword" type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required disabled={isLoading} autoComplete="new-password" />
               <button type="button" style={styles.toggleButton} onClick={() => setShowNew(!showNew)}>
                {showNew ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
           <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm New Password</label>
            <div style={styles.inputWrapper}>
              <input style={styles.input} id="confirmPassword" type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={isLoading} autoComplete="new-password" />
               <button type="button" style={styles.toggleButton} onClick={() => setShowConfirm(!showConfirm)}>
                {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          {error && <p style={styles.errorMessage}>{error}</p>}

          <div style={styles.footer}>
            <button type="button" style={{...styles.button, ...styles.cancelButton}} onClick={onClose} disabled={isLoading}>Cancel</button>
            <button type="submit" style={{...styles.button, ...styles.saveButton}} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;